<template>
  <div>
    <div v-if="showOperatorStatistics" class="row">
      <div class="col-md-12">
        <p
          class="text-right text-muted"
          v-if="this.statistics && this.statistics.calculatedAt">
          Fleet statistics calculated:
          <time :datetime="this.statistics.calculatedAt">{{
            this.statistics.calculatedAt | moment("from", "now")
          }}</time>
        </p>
      </div>
    </div>
    <div v-if="showOperatorStatistics" class="row">
      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-card
          type="warning"
          v-bind:icon="activeSubmerchantsCountLoading
              ? 'fa fa-circle-o-notch fa-spin fa-fw'
              : 'fa fa-users fa-fw'
            "
          small-title="Drivers"
          :title="activeSubmerchantsCount">
          <div class="stats" slot="footer">
            <i
              class="fa fa-circle-o-notch fa-spin fa-fw"
              v-if="inactiveSubmerchantsCountLoading"></i>
            <i class="fa fa-user-secret fa-fw" v-else></i>
            {{ inactiveSubmerchantsCount }} inactive
          </div>
        </stats-card>
      </div>

      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-card
          type="success"
          v-bind:icon="salesValueSumLast7DaysLoading
              ? 'fa fa-circle-o-notch fa-spin fa-fw'
              : 'fa fa-hand-paper-o fa-fw'
            "
          small-title="Fares"
          :title="salesValueSumLast7Days">
          <div class="stats" slot="footer">
            <i class="fa fa-calendar-o"></i>
            Last 7 days
          </div>
        </stats-card>
      </div>

      <div v-if="allocatedTerminalsCount" class="col-lg-3 col-md-6 col-sm-6">
        <stats-card
          type="danger"
          v-bind:icon="allocatedTerminalsCountLoading
              ? 'fa fa-circle-o-notch fa-spin fa-fw'
              : 'fa fa-calculator fa-fw'
            "
          small-title="Terminals"
          :title="allocatedTerminalsCount">
          <div class="stats" slot="footer">
            <i class="fa fa-cubes"></i>
            0 unallocated
            <!--  TODO: fetch count in depositories (unallocated) when available -->
          </div>
        </stats-card>
      </div>

      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-card
          type="info"
          v-bind:icon="activeTerminalsCountLoading
              ? 'fa fa-circle-o-notch fa-spin fa-fw'
              : 'fa fa-heart-o fa-fw'
            "
          small-title="Active"
          :title="activeTerminalsCount">
          <div class="stats" slot="footer">
            <i class="fa fa-clock-o"></i>
            Online in last hour
            <small v-if="activeTerminalsCountFetchedAt">
              (checked
              <time :datetime="activeTerminalsCountFetchedAt">{{
                activeTerminalsCountFetchedAt | moment("HH:mm")
              }}</time>)
            </small>
          </div>
        </stats-card>
      </div>
    </div>

    <div v-if="showOperatorStatistics" class="row">
      <div class="col-lg-4 col-sm-6">
        <chart-card
          v-if="salesByDay7DaysChart.data"
          :chart-data="salesByDay7DaysChart.data"
          :chart-options="salesByDay7DaysChart.options">
          <span slot="title">{{ salesByDay7DaysChart.meta.summary }}</span>
          <badge
            slot="title-label"
            :type="salesByDay7DaysChart.meta.change > 0 ? 'success' : 'danger'">
            <span v-if="salesByDay7DaysChart.meta.change > 0">+</span>
            {{ salesByDay7DaysChart.meta.change }} &#37;
          </badge>

          <template slot="footer-title">
            <router-link to="/transactions/sales" class="text-info">
              View fares data
              <span class="pull-right">
                <i class="fa fa-chevron-right"></i>
              </span>
            </router-link>
          </template>
        </chart-card>
      </div>

      <div class="col-lg-4 col-sm-6">
        <chart-card
          v-if="salesByMonth12MonthsChart.data"
          :chart-data="salesByMonth12MonthsChart.data"
          :chart-options="salesByMonth12MonthsChart.options">
          <span slot="title">{{ salesByMonth12MonthsChart.meta.summary }}</span>
          <badge
            slot="title-label"
            :type="salesByMonth12MonthsChart.meta.change > 0 ? 'success' : 'danger'
              ">
            <span v-if="salesByMonth12MonthsChart.meta.change > 0">+</span>
            {{ salesByMonth12MonthsChart.meta.change }} &#37;
          </badge>

          <template slot="footer-title">
            <router-link to="/transactions/sales" class="text-info">
              View fares data
              <span class="pull-right">
                <i class="fa fa-chevron-right"></i>
              </span>
            </router-link>
          </template>
        </chart-card>
      </div>

      <div class="col-lg-4 col-sm-6">
        <chart-card
          v-if="salesByCardScheme30DaysChart.data"
          :chart-data="salesByCardScheme30DaysChart.data"
          chart-type="Doughnut"
          color="#fbc658"
          chart-title="Card schemes (past 30 days)"
          :title-label="null"
          :chart-options="{
            title: { display: true, text: 'Card schemes (past 30 days)' },
            legend: { display: true, position: 'bottom' },
            tooltips: { enabled: true }
          }">
          <span slot="title">{{
            salesByCardScheme30DaysChart.meta.summary
          }}</span>

          <template slot="footer-title">
            <router-link to="/transactions/sales" class="text-info">
              View more details
              <span class="pull-right">
                <i class="fa fa-chevron-right"></i>
              </span>
            </router-link>
          </template>
        </chart-card>
      </div>
    </div>

    <div v-if="showOperatorStatistics" class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h4 class="card-title">Best and worst-performing drivers</h4>
            <p class="card-category">
              Active drivers in past 30 days (by fare value)
            </p>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-5">
                <h6>Best-performing</h6>
                <div
                  class="table-responsive"
                  style="overflow: auto !important;">
                  <table class="table" v-if="submerchantsSummary.best.length">
                    <tbody>
                      <tr>
                        <th>#</th>
                        <th>Reference</th>
                        <th>Name</th>
                        <th class="text-right">Fares</th>
                        <th class="text-right">Value</th>
                      </tr>
                      <tr
                        v-for="submerchant in submerchantsSummary.best"
                        v-bind:key="submerchant.id">
                        <td>{{ submerchant.rank }}</td>
                        <td>
                          <code>
                              <router-link :to="`/drivers/${submerchant.id}`">{{
                                submerchant.reference
                              }}</router-link>
                            </code>
                        </td>
                        <td>
                          <router-link :to="`/drivers/${submerchant.id}`">{{
                            submerchant.name
                          }}</router-link>
                        </td>
                        <td class="text-right">
                          {{ submerchant.salesThirtyDays.count }}
                        </td>
                        <td class="text-right">
                          {{ submerchant.salesThirtyDays.valueSum }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="col-md-5 offset-md-1">
                <h6>Worst-performing</h6>
                <div
                  class="table-responsive"
                  style="overflow: auto !important;">
                  <table class="table" v-if="submerchantsSummary.worst.length">
                    <tbody>
                      <tr>
                        <th>#</th>
                        <th>Reference</th>
                        <th>Name</th>
                        <th class="text-right">Fares</th>
                        <th class="text-right">Value</th>
                      </tr>
                      <tr
                        v-for="submerchant in submerchantsSummary.worst"
                        v-bind:key="submerchant.id">
                        <td>{{ submerchant.rank }}</td>
                        <td>
                          <code>
                              <router-link :to="`/drivers/${submerchant.id}`">{{
                                submerchant.reference
                              }}</router-link>
                            </code>
                        </td>
                        <td>
                          <router-link :to="`/drivers/${submerchant.id}`">{{
                            submerchant.name
                          }}</router-link>
                        </td>
                        <td class="text-right">
                          {{ submerchant.salesThirtyDays.count }}
                        </td>
                        <td class="text-right">
                          {{ submerchant.salesThirtyDays.valueSum }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer text-right">
            <router-link
              to="/drivers/performance"
              class="text-info btn btn-link">
              Analyse driver performance
              <i class="fa fa-chevron-right"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!showOperatorStatistics" class="row">
      <div class="col-md-12">
        <sales-list></sales-list>
      </div>
    </div>
  </div>
</template>
<script>
import accounting from "accounting";
import moment from "moment";
import _ from "lodash";

import ChartCard from "src/components/UIComponents/Cards/ChartCard";
import StatsCard from "src/components/UIComponents/Cards/StatsCard";
import Badge from "src/components/UIComponents/Badge";
import SalesList from "src/components/Dashboard/Views/Sales/SalesList";
import Card from "src/components/UIComponents/Cards/Card";

export default {
  components: {
    ChartCard,
    Badge,
    SalesList,
    Card,
    StatsCard
  },
  /**
   * Chart data used to render stats, charts. Should be replaced with server data
   */
  data() {
    return {
      statistics: null,
      statisticsLoading: false,
      activeSubmerchantsCount: "...",
      activeSubmerchantsCountLoading: false,
      inactiveSubmerchantsCount: "...",
      inactiveSubmerchantsCountLoading: false,
      allocatedTerminalsCount: "...",
      allocatedTerminalsCountLoading: false,
      activeTerminalsCount: "...",
      activeTerminalsCountLoading: false,
      activeTerminalsCountFetchedAt: null,
      salesValueSumLast7Days: "...",
      salesValueSumLast7DaysLoading: false,
      salesByDay7DaysChart: {
        data: null,
        meta: {
          total: null,
          count: null
        }
      },
      salesByMonth12MonthsChart: {
        data: null,
        meta: {
          summary: null,
          change: null
        }
      },
      salesByCardScheme30DaysChart: {
        data: null,
        meta: {
          summary: null,
          change: null
        }
      },
      submerchantsSummary: {
        best: [],
        worst: []
      }
    };
  },
  computed: {
    operator: function () {
      return this.$store.state.operator;
    },
    operatorUser: function () {
      return this.$store.state.operatorUser;
    },
    showOperatorStatistics: function () {
      const operatorsToHide = [
        "opr_125ikk6vyx0s9", // Avenue Taxis
        "opr_doqgnkhq8zk71", // Fleet Cars
        "opr_1f6mml5giv7u3", // Street Cars Manchester
        "opr_jexnkhd7v29p", // Capital Cars
        "opr_125ikk6w4au5s" // 	Central Taxis (North East)
      ];
      if (
        _.includes(operatorsToHide, this.operator.id) &&
        this.operatorUser.operatorRole === "viewer"
      ) {
        return false;
      } else {
        return true;
      }
    }
  },
  methods: {
    loadOperatorStatistics() {
      this.statisticsLoading = true;
      this.salesValueSumLast7DaysLoading = true;
      const fields = [
        "calculatedAt",
        "sales.sevenDays.currentPeriod.summary",
        "sales.sevenDays.currentPeriod.changeVsPrevious",
        "sales.sevenDays.currentPeriod.segments",
        "sales.sevenDays.previousPeriod.segments",
        "sales.thirtyDays.currentPeriod.summary",
        "sales.thirtyDays.currentPeriod.segments",
        "sales.thirtyDays.previousPeriod.segments",
        "sales.thirtyDays.currentPeriod.changeVsPrevious",
        "sales.twelveMonths.currentPeriod.summary",
        "sales.twelveMonths.currentPeriod.segments",
        "sales.twelveMonths.previousPeriod.segments",
        "sales.twelveMonths.currentPeriod.changeVsPrevious"
      ];
      this.axios
        .get(`v1/operators/${this.operator.id}/statistics`, {
          params: {
            // TODO: limit fetched fields here with fields: ''
            // limit: 10 // only load 10
            fields: fields.join(",")
          }
        })
        .then(response => {
          if (response.status !== 200) {
            this.error = response.statusText;
            return;
          }

          if (response.data) {
            this.statistics = response.data;
            // convenience handling for statistics
            this.salesValueSumLast7Days = accounting.formatMoney(
              this.statistics.sales.sevenDays.currentPeriod.summary.valueSum /
              100,
              "£",
              0
            );
            this.salesValueSumLast7DaysLoading = false;

            // 7 day sales chart
            this.salesByDay7DaysChart = {
              options: {
                title: { display: true, text: "Total fares in past 7 days" },
                legend: {
                  position: "bottom",
                  display: true
                },
                tooltips: {
                  enabled: true,
                  mode: "label",
                  xPadding: 10,
                  yPadding: 10,
                  bodySpacing: 10
                }
              },
              data: {
                labels: _.map(
                  this.statistics.sales.sevenDays.currentPeriod.segments,
                  item => {
                    return moment(item.startDate).format("ddd");
                  }
                ),

                datasets: [
                  {
                    label: `${moment(
                      _.first(
                        this.statistics.sales.sevenDays.currentPeriod.segments
                      ).startDate
                    ).format("DD MMM")} - ${moment(
                      _.last(
                        this.statistics.sales.sevenDays.currentPeriod.segments
                      ).startDate
                    ).format("DD MMM")}`,
                    fill: false,
                    borderColor: "#332288",
                    pointBackgroundColor: "#332288",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    data: _.map(
                      this.statistics.sales.sevenDays.currentPeriod.segments,
                      item => {
                        return item.summary.count;
                      }
                    )
                  },
                  {
                    label: `${moment(
                      _.first(
                        this.statistics.sales.sevenDays.previousPeriod.segments
                      ).startDate
                    ).format("DD MMM")} - ${moment(
                      _.last(
                        this.statistics.sales.sevenDays.previousPeriod.segments
                      ).startDate
                    ).format("DD MMM")}`,
                    fill: false,
                    borderColor: "#88CCEE",
                    pointBackgroundColor: "#88CCEE",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    data: _.map(
                      this.statistics.sales.sevenDays.previousPeriod.segments,
                      item => {
                        return item.summary.count;
                      }
                    )
                  }
                ]
              },
              meta: {
                summary: `${accounting.formatMoney(
                  this.statistics.sales.sevenDays.currentPeriod.summary
                    .valueSum / 100,
                  "£",
                  0
                )}`,
                change: (
                  (this.statistics.sales.sevenDays.currentPeriod
                    .changeVsPrevious.count || 0) * 100
                ).toFixed(2)
              }
            };

            // 12 months sales chart
            this.salesByMonth12MonthsChart = {
              options: {
                title: { display: true, text: "Total fares in past 12 months" },
                legend: {
                  position: "bottom",
                  display: true
                },
                tooltips: {
                  enabled: true,
                  mode: "label",
                  xPadding: 10,
                  yPadding: 10,
                  bodySpacing: 10
                }
              },
              data: {
                labels: _.map(
                  this.statistics.sales.twelveMonths.currentPeriod.segments,
                  item => {
                    return moment(item.startDate).format("MMM");
                  }
                ),
                datasets: [
                  {
                    label: `${moment(
                      _.first(
                        this.statistics.sales.twelveMonths.currentPeriod
                          .segments
                      ).startDate
                    ).format("MMM YYYY")} - ${moment(
                      _.last(
                        this.statistics.sales.twelveMonths.currentPeriod
                          .segments
                      ).startDate
                    ).format("MMM YYYY")}`,
                    fill: false,
                    borderColor: "#117733",
                    pointBackgroundColor: "#117733",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    data: _.map(
                      this.statistics.sales.twelveMonths.currentPeriod.segments,
                      item => {
                        return item.summary.count;
                      }
                    )
                  },
                  {
                    label: `${moment(
                      _.first(
                        this.statistics.sales.twelveMonths.previousPeriod
                          .segments
                      ).startDate
                    ).format("MMM YYYY")} - ${moment(
                      _.last(
                        this.statistics.sales.twelveMonths.previousPeriod
                          .segments
                      ).startDate
                    ).format("MMM YYYY")}`,
                    fill: false,
                    borderColor: "#44AA99",
                    pointBackgroundColor: "#44AA99",
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    data: _.map(
                      this.statistics.sales.twelveMonths.previousPeriod
                        .segments,
                      item => {
                        return item.summary.count;
                      }
                    )
                  }
                ]
              },
              meta: {
                summary: `${accounting.formatMoney(
                  this.statistics.sales.twelveMonths.currentPeriod.summary
                    .valueSum / 100,
                  "£",
                  0
                )}`,
                change: (
                  (this.statistics.sales.twelveMonths.currentPeriod
                    .changeVsPrevious.count || 0) * 100
                ).toFixed(2)
              }
            };

            // salesByCardScheme30Days
            this.salesByCardScheme30DaysChart = {
              data: {
                datasets: [
                  {
                    data: _.map(
                      this.statistics.sales.thirtyDays.currentPeriod.summary
                        .cardSchemes,
                      (scheme, key) => {
                        return scheme.count;
                      }
                    ),
                    backgroundColor: _.map(
                      this.statistics.sales.thirtyDays.currentPeriod.summary
                        .cardSchemes,
                      (scheme, key) => {
                        if (key === "american_express") {
                          return "#0372CA";
                        } else if (key === "mastercard") {
                          return "#FE5D22";
                        } else if (key === "visa") {
                          return "#1D2966";
                        } else if (key === "unknown") {
                          return "#ddd"; // TODO: set this properly
                        } else {
                          return "#000"; // TODO: set random hex
                        }
                      }
                    ),
                    hoverBackgroundColor: _.map(
                      this.statistics.sales.thirtyDays.currentPeriod.summary
                        .cardSchemes,
                      (scheme, key) => {
                        if (key === "american_express") {
                          return "#acdafe";
                        } else if (key === "mastercard") {
                          return "#ca3701";
                        } else if (key === "visa") {
                          return "#374ec3";
                        } else if (key === "unknown") {
                          return "#ddd"; // TODO: set this properly
                        } else {
                          return "#000"; // TODO: set random hex
                        }
                      }
                    )
                  }
                ],
                labels: _.map(
                  this.statistics.sales.thirtyDays.currentPeriod.summary
                    .cardSchemes,
                  (scheme, key) => {
                    return scheme.label;
                  }
                )
              },
              meta: {
                summary: `${accounting.formatMoney(
                  this.statistics.sales.thirtyDays.currentPeriod.summary.count,
                  "",
                  0
                )} fares`
              }
            };
          }
        })
        .catch(error => {
          // Request failed.
          this.error = error.response.statusText;
        })
        .finally(() => {
          this.statisticsLoading = false;
        });
    },
    getRankedSubmerchants(ascending = true, limit = 10) {
      return new Promise((resolve, reject) => {
        let sort = "statistics.sales.thirtyDays.currentPeriod.summary.valueSum";
        if (ascending === false) {
          sort = `-${sort}`;
        }

        this.axios
          .get(`v1/submerchants`, {
            params: {
              "statistics.sales.thirtyDays.currentPeriod.summary.valueSum[$gte]": 0,
              limit,
              isActive: true,
              sort,
              expand: "statistics",
              fields:
                "id,name,reference,statistics.sales.thirtyDays.currentPeriod.summary.valueSum,statistics.sales.thirtyDays.currentPeriod.summary.count"
            }
          })
          .then(response => {
            if (response.status !== 200) {
              this.error = response.statusText;
              const err = new Error(this.error);
              return reject(err);
            }

            let count = 0;
            if (response.headers && response.headers["total-count"]) {
              count = response.headers["total-count"];
            }

            return resolve({
              submerchants: response.data,
              total: count // needed to calculate rank
            });
          })
          .catch(error => {
            // Request failed.
            this.error = error.response.statusText;
            return reject(error);
          });
      });
    },
    countActiveSubmerchants() {
      this.activeSubmerchantsCountLoading = true;
      this.axios
        .get(`v1/submerchants`, {
          params: {
            limit: 1, // load min submberchants, we only want count
            fields: "id",
            isActive: true
          }
        })
        .then(response => {
          if (response.status !== 200) {
            this.error = response.statusText;
            return;
          }

          let count = 0;
          if (response.headers && response.headers["total-count"]) {
            count = response.headers["total-count"];
          }

          this.activeSubmerchantsCount = count;
        })
        .catch(error => {
          // Request failed.
          this.error = error.response.statusText;
        })
        .finally(() => {
          this.activeSubmerchantsCountLoading = false;
        });
    },
    countInactiveSubmerchants() {
      this.inactiveSubmerchantsCountLoading = true;
      this.axios
        .get(`v1/submerchants`, {
          params: {
            limit: 1, // load min submberchants, we only want count
            fields: "id",
            isActive: false
          }
        })
        .then(response => {
          if (response.status !== 200) {
            this.error = response.statusText;
            return;
          }

          let count = 0;
          if (response.headers && response.headers["total-count"]) {
            count = response.headers["total-count"];
          }

          this.inactiveSubmerchantsCount = count;
        })
        .catch(error => {
          // Request failed.
          this.error = error.response.statusText;
        })
        .finally(() => {
          this.inactiveSubmerchantsCountLoading = false;
        });
    },
    countAllocatedTerminals() {
      this.allocatedTerminalsCountLoading = true;
      this.axios
        .get(`v1/terminals`, {
          params: {
            limit: 1, // load min items, we only want count
            fields: "id"
          }
        })
        .then(response => {
          if (response.status !== 200) {
            this.error = response.statusText;
            return;
          }

          let count = 0;
          if (response.headers && response.headers["total-count"]) {
            count = response.headers["total-count"];
          }

          this.allocatedTerminalsCount = count;
        })
        .catch(error => {
          // Request failed.
          this.error = error.response.statusText;
        })
        .finally(() => {
          this.allocatedTerminalsCountLoading = false;
        });
    },
    countActiveTerminals() {
      this.activeTerminalsCountLoading = true;
      this.axios
        .get(`v1/terminals`, {
          params: {
            limit: 1, // load min items, we only want count
            fields: "id",
            "lastConnectionAt[$gte]": moment()
              .subtract(1, "hour")
              .toISOString()
          }
        })
        .then(response => {
          if (response.status !== 200) {
            this.error = response.statusText;
            return;
          }

          let count = 0;
          if (response.headers && response.headers["total-count"]) {
            count = response.headers["total-count"];
          }

          this.activeTerminalsCount = count;
        })
        .catch(error => {
          // Request failed.
          this.error = error.response.statusText;
        })
        .finally(() => {
          // show loader to avoid flashing
          setTimeout(() => {
            this.activeTerminalsCountLoading = false;
            this.activeTerminalsCountFetchedAt = moment().toISOString();
          }, 1500);
        });
    }
  },
  beforeMount() { },
  mounted() {
    this.loadOperatorStatistics();
    this.countActiveSubmerchants();
    this.countInactiveSubmerchants();
    this.countAllocatedTerminals();
    this.countActiveTerminals();

    this.getRankedSubmerchants(false, 10)
      .then(result => {
        this.submerchantsSummary.best = _.map(
          result.submerchants,
          (submerchant, index) => {
            const item = {
              id: submerchant.id,
              reference: submerchant.reference,
              name: submerchant.name,
              salesThirtyDays: {
                count:
                  submerchant.statistics.sales.thirtyDays.currentPeriod.summary
                    .count,
                valueSum: accounting.formatMoney(
                  submerchant.statistics.sales.thirtyDays.currentPeriod.summary
                    .valueSum / 100,
                  "£",
                  0
                )
              }
            };

            item.rank = index + 1;
            return item;
          }
        );
      })
      .catch(err => {
        console.error("err: ", err);
      });

    this.getRankedSubmerchants(true, 10)
      .then(result => {
        this.submerchantsSummary.worst = _.map(
          result.submerchants,
          (submerchant, index) => {
            const item = {
              id: submerchant.id,
              reference: submerchant.reference,
              name: submerchant.name,
              salesThirtyDays: {
                count:
                  submerchant.statistics.sales.thirtyDays.currentPeriod.summary
                    .count,
                valueSum: accounting.formatMoney(
                  submerchant.statistics.sales.thirtyDays.currentPeriod.summary
                    .valueSum / 100,
                  "£",
                  0
                )
              }
            };

            item.rank = result.total - index;
            return item;
          }
        );
      })
      .catch(err => {
        console.error("err: ", err);
      });

    // loop to refresh live stats
    setInterval(() => {
      this.countActiveTerminals();
    }, 1000 * 60 * 3); // every three mins
  }
};
</script>
<style></style>
